import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, AtSignIcon, BadgeCheckIcon, BarChart3Icon, BotIcon, BriefcaseIcon, Building2Icon, CalendarDaysIcon, CheckIcon, ChevronDownIcon, ClockIcon, FilterIcon, HeadphonesIcon, LinkIcon, ImageIcon, InstagramIcon, LinkedinIcon, LockIcon, MagnetIcon, MapPinIcon, MegaphoneIcon, MessageCircleIcon, PhoneIcon, SendIcon, ShieldCheckIcon, SparklesIcon, TargetIcon, TrendingUpIcon, UsersIcon, VideoIcon, XIcon, YoutubeIcon, ZapIcon } from 'lucide-react';
import { Form, LandingBlock, LandingBlockType, LandingPage } from '../../types';
import { blockDefinition } from '../../data/blockLibrary';
import { BrandLogo } from '../shared/BrandLogo';
import { Button } from '../ui/Button';
import { VideoReplay } from './VideoReplay';
import { SOCIAL_NETWORKS } from '../../data/socialNetworks';
import type { DynamicFormResult } from './DynamicForm';
import type { GuaranteeItem } from './GuaranteeAccordion';
import { backgroundClass } from '../../data/background';
import { loadFont } from '../../utils/fonts';
import { perfSolo } from '../../utils/perf';
/*
 * Rendimiento: el formulario, el acordeón de garantías y los elementos
 * flotantes están por debajo del pliegue y no hacen falta para la primera
 * pintura (logo, titular, subtítulo, miniatura del vídeo y CTA). Antes se
 * importaban de forma estática, así que el visitante descargaba framer-motion
 * (que usa DynamicForm) antes de ver el hero. Ahora se cargan en su propio
 * fragmento, en cuanto el navegador tiene hueco.
 */
const DynamicForm = React.lazy(() => import('./DynamicForm').then(m => ({
  default: m.DynamicForm
})));
const GuaranteeAccordion = React.lazy(() => import('./GuaranteeAccordion').then(m => ({
  default: m.GuaranteeAccordion
})));
const FloatingElements = React.lazy(() => import('./FloatingElements').then(m => ({
  default: m.FloatingElements
})));
const ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  Target: TargetIcon,
  Users: UsersIcon,
  Video: VideoIcon,
  Megaphone: MegaphoneIcon,
  Sparkles: SparklesIcon,
  Bot: BotIcon,
  BarChart3: BarChart3Icon,
  Building2: Building2Icon,
  CalendarDays: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Briefcase: BriefcaseIcon,
  Bot: BotIcon,
  Magnet: MagnetIcon,
  Filter: FilterIcon,
  TrendingUp: TrendingUpIcon,
  ShieldCheck: ShieldCheckIcon,
  Zap: ZapIcon,
  BadgeCheck: BadgeCheckIcon,
  Headphones: HeadphonesIcon,
  Lock: LockIcon,
  Clock: ClockIcon
};
interface LandingRendererProps {
  landing: LandingPage;
  form?: Form;
  onSubmit?: (result: DynamicFormResult) => Promise<void>;
  preview?: boolean;
  /** Muestra los accesos de la plataforma en la barra superior (home de AVA). */
  authLinks?: boolean;
  /** Bloque resaltado en el constructor visual. */
  selectedBlockId?: string | null;
  /** Permite seleccionar un bloque haciendo clic sobre él en la vista previa. */
  onSelectBlock?: (blockId: string) => void;
  /**
   * Dispositivo simulado en la vista previa del constructor. Con 'mobile' se
   * aplican los mismos ajustes de móvil que en la landing pública.
   */
  viewport?: 'desktop' | 'tablet' | 'mobile';
  /**
   * Nombre de la empresa. Se usa como identificador cuando la landing y la
   * organización no tienen logotipo, en lugar de la marca Sistema AVA.
   */
  brandName?: string;
}
const SPACING: Record<LandingBlock['spacing'], string> = {
  compacto: 'py-10 sm:py-14',
  normal: 'py-16 sm:py-24',
  amplio: 'py-24 sm:py-32'
};

/**
 * Destino del CTA principal: el formulario multipaso de cualificación. Si la
 * landing no lo incluye, baja al calendario.
 */
function scrollToForm() {
  const target = document.getElementById('formulario') ?? document.getElementById('calendario');
  target?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
function cfg<T>(block: LandingBlock, key: string, fallback: T): T {
  const value = block.config[key];
  return (value === undefined || value === null ? fallback : value) as T;
}

/** Interruptores del constructor: guardan «si» / «no» o un booleano. */
function flag(block: LandingBlock, key: string, fallback: boolean): boolean {
  const value = block.config[key];
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return String(value) !== 'no';
}
function blockLabel(type: LandingBlockType): string {
  return blockDefinition(type).label;
}
const SHADOWS: Record<string, string> = {
  ninguna: 'none',
  suave: '0 4px 20px -6px rgba(11,31,77,0.10)',
  media: '0 14px 36px -12px rgba(11,31,77,0.20)',
  fuerte: '0 28px 64px -18px rgba(11,31,77,0.30)'
};
const BACKGROUNDS: Record<string, string | undefined> = {
  transparente: undefined,
  blanco: '#FFFFFF',
  gris: '#F5F7FA',
  marca: 'var(--primary)',
  oscuro: '#0B1F4D'
};

/**
 * Ajustes de móvil del bloque: se publican como variables CSS y una clase por
 * propiedad definida, para que `index.css` las aplique solo bajo 640 px. Así la
 * versión móvil se edita sin alterar escritorio ni tablet, y el constructor y
 * la landing pública comparten exactamente las mismas reglas.
 */
function deviceStyle(block: LandingBlock, device: 'm' | 't'): {
  vars: Record<string, string>;
  className: string;
} {
  const s = device === 'm' ? block.style?.mobile : block.style?.tablet;
  if (!s || s.sync !== false) return {
    vars: {},
    className: ''
  };
  const vars: Record<string, string> = {};
  const classes: string[] = [];
  const add = (key: string, value: string | undefined) => {
    if (value === undefined) return;
    vars[`--${device}-${key}`] = value;
    classes.push(`ava-${device}-${key}`);
  };
  add('title', s.titleSize !== undefined ? `${s.titleSize}px` : undefined);
  add('body', s.bodySize !== undefined ? `${s.bodySize}px` : undefined);
  add('lh', s.lineHeight !== undefined ? String(s.lineHeight) : undefined);
  add('ls', s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined);
  add('weight', s.titleWeight !== undefined ? String(s.titleWeight) : undefined);
  add('align', s.align);
  add('py', s.paddingY !== undefined ? `${s.paddingY}px` : undefined);
  add('px', s.paddingX !== undefined ? `${s.paddingX}px` : undefined);
  return {
    vars,
    className: classes.join(' ')
  };
}

/** Ajustes responsive (móvil + tablet) combinados de un bloque. */
function mobileStyle(block: LandingBlock): {
  vars: React.CSSProperties;
  className: string;
} {
  const mobile = deviceStyle(block, 'm');
  const tablet = deviceStyle(block, 't');
  // Tamaños de escritorio: mismo mecanismo (variable CSS + clase), así que
  // constructor, vista previa y landing pública comparten exactamente el
  // mismo cálculo. Móvil y tablet los sobrescriben en su media query.
  const desktopVars: Record<string, string> = {};
  const desktopClasses: string[] = [];
  if (block.style?.titleSize !== undefined) {
    desktopVars['--d-title'] = `${block.style.titleSize}px`;
    desktopClasses.push('ava-d-title');
  }
  if (block.style?.bodySize !== undefined) {
    desktopVars['--d-body'] = `${block.style.bodySize}px`;
    desktopClasses.push('ava-d-body');
  }
  return {
    vars: {
      ...desktopVars,
      ...mobile.vars,
      ...tablet.vars
    } as React.CSSProperties,
    className: `${desktopClasses.join(' ')} ${mobile.className} ${tablet.className}`.trim()
  };
}

/** Traduce el estilo editable del bloque a CSS en línea. */
function wrapperStyle(block: LandingBlock): React.CSSProperties {
  const s = block.style;
  if (!s) return {};
  return {
    backgroundColor: s.background ? BACKGROUNDS[s.background] : undefined,
    color: s.textColor,
    paddingTop: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingBottom: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingLeft: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    paddingRight: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    borderRadius: s.radius !== undefined ? `${s.radius}px` : undefined,
    boxShadow: s.shadow ? SHADOWS[s.shadow] : undefined,
    textAlign: s.align,
    letterSpacing: s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined,
    fontWeight: s.titleWeight,
    textTransform: s.uppercase ? 'uppercase' : undefined,
    overflow: s.radius ? 'hidden' : undefined
  };
}
const BUTTON_ANIMATION: Record<string, string> = {
  ninguna: '',
  elevar: 'hover:-translate-y-0.5',
  escalar: 'hover:scale-[1.03]',
  pulso: 'animate-pulse'
};
const BUTTON_ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  ArrowRight: ArrowRightIcon,
  Sparkles: SparklesIcon,
  Send: SendIcon,
  Calendar: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Phone: PhoneIcon
};

/** Botón de un bloque, totalmente personalizable desde el constructor. */
function BlockCta({
  block,
  label,
  onClick
}: {
  block: LandingBlock;
  label: string;
  onClick: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  const b = block.button ?? {};
  const Icon = b.icon ? BUTTON_ICONS[b.icon] : undefined;
  const style: React.CSSProperties = {
    backgroundColor: hover && b.hoverColor ? b.hoverColor : b.color,
    color: b.textColor,
    borderRadius: b.radius !== undefined ? `${b.radius}px` : undefined,
    height: b.height !== undefined ? `${b.height}px` : undefined,
    paddingLeft: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    paddingRight: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    width: b.fullWidth ? '100%' : undefined
  };
  return <Button size="lg" onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={style} className={`h-14 w-full rounded-xl px-8 transition-transform duration-200 sm:w-auto ${b.shadow === false ? '' : 'ava-glow'} ${BUTTON_ANIMATION[b.animation ?? 'elevar'] ?? ''}`} rightIcon={Icon ? <Icon className="h-4 w-4" data-id="element-599" data-mp="1ul8t1g3a7np8" /> : undefined} data-id="element-598" data-mp="3g0oeek87vlve">
      {b.label || label}
    </Button>;
}

/** Resalta en azul uno o varios fragmentos, separados por "|". */
function Highlighted({
  text,
  highlight
}: {
  text: string;
  highlight: string;
}) {
  const segments = highlight.split('|').map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return <>{text}</>;
  const escaped = segments.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'g'));
  return <>
      {parts.map((part, index) => segments.includes(part) ? <span key={`${part}-${index}`} className="text-primary" data-id="element-600" data-mp="34y8aj3muhuv1">
            {part}
          </span> : <React.Fragment key={`t-${index}`} data-id="element-601" data-mp="34vvl65erci7y">{part}</React.Fragment>)}
    </>;
}

/** Anotación manuscrita con flecha apuntando al vídeo. */
function HandwrittenNote({
  text,
  color,
  size,
  mobileSize,
  arrowSize,
  align = 'center',
  width,
  gapTop
}: {
  text: string;
  color?: string;
  /** Tamaño del texto en escritorio (px). Vacío = 24 px. */
  size?: number;
  /** Tamaño del texto en móvil (px), independiente de escritorio. */
  mobileSize?: number;
  /** Ancho de la flecha en móvil (px). La altura se calcula en proporción. */
  arrowSize?: number;
  align?: 'left' | 'center' | 'right';
  /** Ancho máximo del bloque en móvil (px). */
  width?: number;
  /** Separación con el vídeo (px). */
  gapTop?: number;
}) {
  /*
    Contenedor responsive (nunca posición absoluta), así la flecha se ve
    completa, centrada y sin solaparse con el vídeo ni con el botón.
  */
  /*
    Tamaños móviles en las clases base (sin media queries propias), así la
    flecha se ve completa también en la landing publicada aunque no se cargue
    ninguna hoja de estilos adicional.
  */
  /*
    Los tamaños viajan como variables CSS y los aplica `index.css`: así el
    tamaño de móvil es independiente del de escritorio y se ve igual en la
    vista previa del constructor y en la landing publicada.
  */
  const vars = {
    '--note-size': size ? `${size}px` : undefined,
    '--note-size-m': mobileSize ? `${mobileSize}px` : undefined,
    '--note-arrow-m': arrowSize ? `${arrowSize}px` : undefined,
    '--note-width-m': width ? `${width}px` : undefined,
    '--note-gap': gapTop !== undefined ? `${gapTop}px` : undefined,
    color: color || undefined
  } as React.CSSProperties;
  const justify = align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';
  return <div className={`ava-note mx-auto mt-4 flex w-full max-w-md items-center gap-1.5 overflow-hidden px-4 sm:mt-6 sm:max-w-none sm:gap-3 ${justify}`} style={vars} data-id="element-602" data-mp="2yj82a9f0z33">
      <svg viewBox="0 0 48 34" fill="none" aria-hidden="true" className="ava-note-arrow h-[24px] w-[34px] flex-none text-primary sm:h-[34px] sm:w-12" data-id="element-603" data-mp="3p9ngbtmp2fka">
        <path d="M46 30C33 33 12 31 4 20C1.5 16.4 2.5 10 8 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" data-id="element-604" data-mp="3g75q743x52s0" />
        <path d="M4 2.5L8.2 6.2L3.4 9.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-605" data-mp="19hzk60fi8r2j" />
      </svg>
      <p className="ava-note-text min-w-0 break-words font-hand text-[17px] leading-tight text-primary sm:text-2xl" data-id="element-606" data-mp="1edye03jlr1ac">
        {text}
      </p>
    </div>;
}
export function LandingRenderer({
  landing,
  form,
  onSubmit,
  preview = false,
  authLinks = false,
  selectedBlockId = null,
  onSelectBlock,
  viewport = 'desktop',
  brandName
}: LandingRendererProps) {
  const config = landing.pageConfig;
  const theme = config.theme;
  const cardShadow = theme === 'minimalista' ? '' : 'shadow-soft';
  /*
   * Herencia del fondo global. Cuando la landing tiene un efecto de fondo
   * activo, las secciones dejan de pintar su franja opaca para que el efecto
   * se vea de forma continua detrás de TODA la página (una sola capa global,
   * nunca una por bloque). Un bloque que declare «fondo propio» en su estilo
   * lo sigue aplicando: ese color viaja en el wrapper y manda sobre esta
   * herencia. Vale para los bloques actuales y para los que se añadan después.
   */
  const dynamicBg = (config.background?.type ?? 'normal') !== 'normal';
  const surfaceClass = dynamicBg ? '' : theme === 'claro_corporativo' ? 'bg-surface' : 'bg-background';
  /*
   * Prueba de aislamiento (`?ava-solo=1`): la landing se renderiza SOLO con la
   * barra superior y el hero. Sin formulario, sin vídeo, sin flotantes, sin
   * garantías y sin fondo. Si con eso sigue tardando, el retraso no está en
   * esos componentes sino en la consulta inicial, el paquete o el hosting.
   */
  const solo = !preview && perfSolo();
  const visibles = config.blocks.filter(b => b.visible);
  const blocks = solo ? visibles.filter(b => b.type === 'topbar' || b.type === 'logo' || b.type === 'hero' || b.type === 'titulo' || b.type === 'subtitulo') : visibles;
  const rootRef = React.useRef<HTMLDivElement>(null);

  /*
   * Tipografías bajo demanda: solo se pide la familia que esta landing usa de
   * verdad, con un <link> que no bloquea la primera pintura. Caveat es la
   * anotación manuscrita del bloque de vídeo, así que se carga únicamente si
   * ese bloque existe y es visible.
   */
  React.useEffect(() => {
    loadFont(config.fontFamily);
    if (visibles.some(b => b.type === 'video')) loadFont('Caveat', '500;600');
  }, [config.fontFamily, visibles]);

  // Al seleccionar un bloque en el constructor, la vista previa se desplaza
  // suavemente hasta él para mantener panel y preview sincronizados.
  React.useEffect(() => {
    if (!selectedBlockId || !onSelectBlock) return;
    const target = rootRef.current?.querySelector(`[data-block-id="${selectedBlockId}"]`);
    target?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, [selectedBlockId, onSelectBlock]);

  /** Logotipo de la empresa, con altura configurable por bloque. */
  function mark(height = 30, mobileHeight?: number) {
    return <BrandLogo src={config.logoUrl} size={height} mobileSize={mobileHeight} alt={brandName || landing.name} fallbackName={brandName} data-id="element-607" data-mp="28ttznmwxl33e" />;
  }
  const brandMark = mark();
  /**
   * Logotipo con altura propia en móvil. Se resuelve con clases (`sm:`) y no
   * midiendo la ventana, así la vista previa del constructor y la landing
   * pública muestran exactamente lo mismo.
   */
  function responsiveMark(height: number, mobileHeight?: number) {
    return mark(height, mobileHeight);
  }
  function renderBlock(block: LandingBlock) {
    switch (block.type) {
      case 'topbar':
        {
          const ctaLabel = cfg(block, 'ctaLabel', '');
          const centered = cfg(block, 'logoAlign', 'center') === 'center';
          const showLogo = flag(block, 'showLogo', true);
          const logoHeight = Number(cfg(block, 'logoHeight', 34)) || 34;
          const logoHeightMobile = Number(cfg(block, 'logoHeightMobile', 0)) || 0;
          const hideLogoMobile = !flag(block, 'showLogoMobile', true);
          const headerMark = hideLogoMobile ? <span className="hidden sm:block" data-id="element-608" data-mp="2vvfgzlth7qvp">{mark(logoHeight)}</span> : responsiveMark(logoHeight, logoHeightMobile);
          // El acceso «Iniciar sesión» se puede ocultar desde el editor sin
          // dejar espacio vacío: si no hay nada que mostrar, no se renderiza.
          const showAuth = authLinks && flag(block, 'showAuth', true);
          const actions = <div className="flex items-center gap-2 sm:gap-4" data-id="element-609" data-mp="yxrphr4gj3k4">
            {showAuth && <Link to="/login" className="whitespace-nowrap text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground sm:text-sm" data-id="element-610" data-mp="z042dpvcynqb">
                Iniciar sesión
              </Link>}
            {ctaLabel && <Button size="sm" onClick={scrollToForm} className="whitespace-nowrap rounded-full px-4 text-[13px] sm:px-5 sm:text-sm" data-id="element-611" data-mp="1aiwzc61gu8m">
                {ctaLabel}
              </Button>}
          </div>;
          const hasActions = Boolean(ctaLabel) || showAuth;
          return <header key={block.id} className="sticky top-0 z-30 w-full border-b border-border/70 bg-background/80 backdrop-blur-xl" data-id="element-612" data-mp="b75js6hxdvda">
            {centered ?
            // Logo centrado: las acciones se reservan su propio espacio a
            // izquierda y derecha, así nunca se solapan con el logotipo.
            <div className="mx-auto grid max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 py-3 sm:gap-3 sm:px-8 sm:py-4" data-id="element-613" data-mp="3g488ltbxixtr">
                <span aria-hidden="true" data-id="element-614" data-mp="3q3z2ptwkvatb" />
                <div className="flex shrink-0 justify-center" data-id="element-615" data-mp="3q2xsfrndyscn">{showLogo && headerMark}</div>
                <div className="flex justify-end" data-id="element-616" data-mp="3uw0jnt4oxppo">{hasActions && actions}</div>
              </div> : <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-8 sm:py-4" data-id="element-617" data-mp="2qenasktyuxb">
                {showLogo ? headerMark : <span data-id="element-618" data-mp="7scdk6rnepzr" />}
                {hasActions && actions}
              </div>}
          </header>;
        }
      case 'logo':
        return <div key={block.id} className="flex justify-center px-5 pt-14" data-id="element-619" data-mp="hoyxmtc5hd0q">
            {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
          </div>;
      case 'hero':
        {
          const title = cfg(block, 'title', '');
          const eyebrow = cfg(block, 'eyebrow', '');
          return <section key={block.id} className="px-5 pb-4 pt-9 text-center sm:px-8 sm:pb-6 sm:pt-20" data-id="element-620" data-mp="10lhafb0hpsa2">
            <div className="ava-reveal mx-auto w-full max-w-4xl" data-id="element-621" data-mp="vtpiox5llgic">
              {flag(block, 'showLogo', false) && <div className="mb-8 flex justify-center sm:mb-12" data-id="element-622" data-mp="rrug7pbxkx2z">
                  {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
                </div>}
              {eyebrow && <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary sm:text-xs sm:tracking-[0.3em]" data-id="element-623" data-mp="1jjti0sbr6u3j">
                  {eyebrow}
                </p>}
              {cfg(block, 'brandLine', '') && <p className="mx-auto mt-2.5 max-w-[19rem] text-[10px] font-bold uppercase leading-[1.5] tracking-[0.14em] text-navy sm:mt-4 sm:max-w-none sm:text-[13px] sm:tracking-[0.3em]" data-id="element-624" data-mp="1aivbo3dueahs">
                  {cfg(block, 'brandLine', '')}
                </p>}
              {/*
                Móvil: 30–34 px según el ancho real del dispositivo (32,8 px en
                un iPhone de 390 px) con interlineado 1.06. Compacto y elegante,
                deja respirar el vídeo y el resto del hero. Este es solo el
                valor por defecto: «Estilo → Móvil → Tamaño del título» lo
                sobreescribe con !important en el rango 18–64 px.
                Los saltos de línea manuales de la plantilla se respetan solo
                desde tablet: en móvil el texto fluye y decide sus propios
                cortes. Escritorio queda exactamente igual que antes.
               */}
              <h1 className="mx-auto mt-4 w-[93%] max-w-3xl whitespace-normal text-[clamp(30px,8.4vw,34px)] font-extrabold leading-[1.06] tracking-[-0.024em] text-navy sm:mt-9 sm:w-full sm:whitespace-pre-line sm:text-[54px] sm:font-bold sm:leading-[1.08] sm:tracking-[-0.025em]" data-id="element-625" data-mp="jrrxg1ug52it">
                <Highlighted text={title} highlight={cfg(block, 'highlight', '')} data-id="element-626" data-mp="9vye9zm82as2" />
              </h1>
              {/* Subtítulo ancho (88–92 % en móvil, dos líneas en escritorio),
                  gris elegante y con «15 días» resaltado. */}
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-3.5 w-[92%] max-w-[38rem] text-[17px] leading-[1.45] text-muted-foreground sm:mt-7 sm:w-full sm:max-w-2xl sm:text-[17px] sm:leading-relaxed" data-id="element-627" data-mp="1a3d6ub64vqeu">
                  <Highlighted text={cfg(block, 'subtitle', '')} highlight={cfg(block, 'subtitleHighlight', '')} data-id="element-628" data-mp="1jz6jdujurjwm" />
                </p>}
              {cfg(block, 'extraLine', '') && <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground" data-id="element-629" data-mp="1tq1ezpiznvuq">
                  {cfg(block, 'extraLine', '')}
                </p>}
            </div>
          </section>;
        }
      case 'titulo':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-630" data-mp="22rhj5fj7n1qi">
            <h2 className="mx-auto max-w-3xl text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-631" data-mp="27q2i7bmq7fdd">
              {cfg(block, 'text', '')}
            </h2>
          </section>;
      case 'subtitulo':
        return <section key={block.id} className="px-5 pb-8" data-id="element-632" data-mp="dcb0rpy5nr7d">
            <p className="mx-auto max-w-2xl text-center text-lg text-muted-foreground" data-id="element-633" data-mp="5ak5xd0qgeua">
              {cfg(block, 'text', '')}
            </p>
          </section>;
      case 'imagen':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-634" data-mp="f8zy7jbhxu5d">
            {cfg(block, 'url', '') ? <img src={cfg(block, 'url', '')} alt={cfg(block, 'alt', '')} loading="lazy" decoding="async" className={`mx-auto w-full max-w-5xl rounded-2xl object-cover ${cardShadow}`} data-id="element-635" data-mp="a9wsrxrltlwv" /> : <div className="mx-auto flex aspect-video w-full max-w-5xl items-center justify-center rounded-2xl border border-dashed border-border bg-surface text-sm text-muted-foreground" data-id="element-636" data-mp="o7ld94hrer1e">
                Añade una imagen desde el editor
              </div>}
          </section>;
      case 'video':
        {
          const num = (key: string): number | undefined => {
            const raw = String(block.config[key] ?? '').trim();
            if (raw === '') return undefined;
            const parsed = Number.parseInt(raw, 10);
            return Number.isFinite(parsed) ? parsed : undefined;
          };
          const frame = {
            radius: num('radius'),
            radiusTablet: num('radiusTablet'),
            radiusMobile: num('radiusMobile'),
            radiusCustom: cfg(block, 'radiusCustom', ''),
            borderWidth: num('borderWidth'),
            borderColor: cfg(block, 'borderColor', ''),
            shadow: (cfg(block, 'shadowStyle', '') || undefined) as 'ninguna' | 'suave' | 'media' | 'fuerte' | undefined,
            maxWidth: num('maxWidth')
          };
          const gap = num('gap');
          return <section key={block.id} className="ava-reveal ava-reveal-1 px-4 pb-4 pt-7 sm:px-8 sm:pb-8 sm:pt-10" style={gap === undefined ? undefined : {
            paddingTop: gap,
            paddingBottom: gap
          }} data-id="element-637" data-mp="tw8egyjnwpo7">
            <VideoReplay url={cfg(block, 'url', '')} thumbnailUrl={cfg(block, 'thumbnailUrl', '')} overlayTitle={cfg(block, 'overlayTitle', '') ? <Highlighted text={cfg(block, 'overlayTitle', '')} highlight={cfg(block, 'overlayHighlight', '')} data-id="element-639" data-mp="oyq1mx2ixqax" /> : undefined} maxWidthClass="max-w-[760px]" editing={preview} frame={frame} data-id="element-638" data-mp="jytyf01ni2wf" />
            {cfg(block, 'note', '') && cfg(block, 'showNote', 'si') !== 'no' && <HandwrittenNote text={cfg(block, 'note', '')} color={cfg(block, 'noteColor', '')} size={Number(cfg(block, 'noteSize', 0)) || undefined} mobileSize={Number(cfg(block, 'noteSizeMobile', 0)) || undefined} arrowSize={Number(cfg(block, 'noteArrowMobile', 0)) || undefined} align={cfg(block, 'noteAlign', 'center') as 'left' | 'center' | 'right'} width={Number(cfg(block, 'noteWidthMobile', 0)) || undefined} gapTop={cfg(block, 'noteGap', '') === '' ? undefined : Number(cfg(block, 'noteGap', 0))} data-id="element-640" data-mp="1igoeynqyapae" />}
            {cfg(block, 'caption', '') && <p className="mx-auto mt-6 max-w-2xl text-center text-base text-muted-foreground" data-id="element-641" data-mp="ulviga0nz52s">
                {cfg(block, 'caption', '')}
              </p>}
            {cfg(block, 'ctaLabel', '') && <div className="mx-auto mt-9 flex max-w-[380px] justify-center" data-id="element-642" data-mp="2oyl9mjzne06h">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', '')} onClick={scrollToForm} data-id="element-643" data-mp="2wgjiqndx3996" />
              </div>}
          </section>;
        }
      case 'beneficios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          const singleRowBenefits = String(cfg(block, 'mobileLayout', '')) === 'fila' && items.length === 3;
          /*
            Centrado real de las tarjetas. Antes la rejilla era siempre de
            cuatro columnas, así que con dos, tres o cinco beneficios la última
            fila quedaba pegada a la izquierda y el bloque se veía descuadrado.
            Ahora es un flex centrado con anchos fijos por dispositivo: las
            tarjetas quedan simétricas y con la misma separación sea cual sea
            el número de elementos, y la fila incompleta se centra sola.
          */
          const BENEFIT_WIDTH: Record<number, string> = {
            1: 'lg:w-[360px]',
            2: 'lg:w-[calc(50%-12px)]',
            3: 'lg:w-[calc(33.333%-16px)]'
          };
          const benefitWidth = BENEFIT_WIDTH[items.length] ?? 'lg:w-[calc(25%-18px)]';
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-644" data-mp="2csrwqk2c64ab">
            <div className="mx-auto flex max-w-6xl flex-col items-center" data-id="element-645" data-mp="28s7zlghrj0o6">
              {cfg(block, 'title', '') && <h2 className="mb-4 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-646" data-mp="1l2dp35kx1o73">
                  {cfg(block, 'title', '')}
                </h2>}
              {cfg(block, 'subtitle', '') && <p className="mx-auto max-w-2xl text-center text-[15px] leading-relaxed text-muted-foreground sm:text-lg" data-id="element-647" data-mp="1tvqqyx0yk05b">
                  {cfg(block, 'subtitle', '')}
                </p>}
              {/* Si el bloque pide una única fila en móvil (los tres pilares
                  creados como «beneficios» en landings antiguas), las clases
                  base pasan a tres columnas compactas. En cualquier otro caso
                  el bloque se comporta exactamente como antes. */}
              <div className={`mt-10 flex w-full flex-wrap items-stretch justify-center sm:mt-14 sm:gap-6 ${singleRowBenefits ? 'gap-1.5' : 'gap-6'}`} data-id="element-648" data-mp="s0npety78rli">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  const short = String((item as {
                    mobileText?: string;
                  }).mobileText ?? '').trim();
                  return <article key={`${item.title}-${index}`} className={`group flex min-w-0 flex-col items-center rounded-2xl border border-border bg-background text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 sm:w-[calc(50%-12px)] sm:p-8 ${benefitWidth} ${singleRowBenefits ? 'w-[calc(33.333%-4px)] p-3' : 'w-full p-8'} ${cardShadow}`} data-id="element-649" data-mp="1aw4d3qug959j" data-mpi={index}>
                      <span className={`mx-auto flex items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/15 sm:mb-6 sm:h-14 sm:w-14 ${singleRowBenefits ? 'mb-2.5 h-[46px] w-[46px]' : 'mb-6 h-14 w-14'}`} data-id="element-650" data-mp="3lr1hhzgh29q1">
                        <Icon className={`text-primary sm:h-6 sm:w-6 ${singleRowBenefits ? 'h-[24px] w-[24px]' : 'h-6 w-6'}`} data-id="element-651" data-mp="2t1jlxxvrc94a" />
                      </span>
                      <h3 className={`break-words font-semibold text-navy sm:mb-3 sm:text-base sm:leading-normal ${singleRowBenefits ? 'mb-1.5 text-[12px] leading-[1.15]' : 'mb-3 text-base'}`} data-id="element-652" data-mp="2p1ivtfzaq9he">
                        {item.title}
                      </h3>
                      <p className={`break-words text-muted-foreground sm:text-sm sm:leading-relaxed ${singleRowBenefits ? 'text-[10px] leading-[1.3]' : 'text-sm leading-relaxed'}`} data-id="element-653" data-mp="3bzvt5e33z8x8">
                        {singleRowBenefits && short ? <>
                            <span className="sm:hidden" data-id="element-654" data-mp="8cvkvx3gfnx0">{short}</span>
                            <span className="hidden sm:inline" data-id="element-655" data-mp="3jj6sgweizii">{item.text}</span>
                          </> : item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'pilares':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          /*
            Panel blanco con sombra suave, iconos azules sobre círculo claro y
            divisores verticales sutiles. En móvil los tres pilares caben en una
            única fila (tres columnas de un tercio del ancho), sin carrusel ni
            scroll horizontal. Los tamaños móviles se editan desde el bloque.
          */
          const singleRow = String(cfg(block, 'mobileLayout', 'fila')) !== 'apilado';
          const showMobileText = flag(block, 'showMobileText', true);
          /** Valor numérico del bloque, con respaldo si está vacío o mal escrito. */
          const px = (key: string, fallback: number) => {
            const parsed = Number.parseInt(String(block.config[key] ?? ''), 10);
            return `${Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback}px`;
          };
          const pillarVars = {
            '--pillar-gap': px('mobileGap', 6),
            '--pillar-pad': px('mobilePadding', 2),
            '--pillar-circle': px('mobileCircleSize', 46),
            '--pillar-icon': px('mobileIconSize', 24),
            '--pillar-title': px('mobileTitleSize', 12),
            '--pillar-text': px('mobileTextSize', 10)
          } as React.CSSProperties;
          /*
            Las clases base son las de MÓVIL (Tailwind es mobile-first), así que
            la fila de tres columnas no depende de ninguna regla personalizada
            de index.css ni de una media query: viaja en el propio HTML del
            componente que renderiza la landing pública. Las variantes `sm:`
            devuelven el diseño de escritorio exactamente como estaba.
          */
          return <section key={block.id} className="ava-reveal ava-reveal-2 overflow-hidden px-3 py-8 sm:px-8 sm:py-12" data-id="element-656" data-mp="1dtwc5fv79n3y">
            <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-border/70 bg-background px-2 py-7 shadow-soft sm:px-8 sm:py-12" data-id="element-657" data-mp="19subculmx2zv">
              <div className={`grid w-full ${singleRow ? 'ava-pillars grid-cols-3 gap-1.5' : 'grid-cols-1 gap-9'} sm:grid-cols-3 sm:gap-0`} style={pillarVars} data-id="element-658" data-mp="1nixuvdt4qilm">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  // Texto móvil propio; si no existe, se recorta el largo para
                  // que los tres pilares sigan cabiendo en una sola fila.
                  const custom = String((item as {
                    mobileText?: string;
                  }).mobileText ?? '').trim();
                  const short = custom || (item.text.length > 40 ? `${item.text.slice(0, 38).trimEnd()}…` : item.text);
                  /*
                    El separador NO es el borde de la columna (dependería de la
                    altura de su texto): es un pseudo-elemento con top y bottom
                    uniformes, centrado en la línea entre columnas. Ver
                    `.ava-pillar-divider` en index.css.
                  */
                  const divider = index > 0 ? singleRow ? 'ava-pillar-divider' : 'sm:border-l sm:border-border/60' : '';
                  return <article key={`${item.title}-${index}`} className={`ava-pillar relative w-full min-w-0 overflow-hidden px-0.5 text-center sm:px-7 ${divider}`} data-id="element-659" data-mp="1zmijdf2bxypd" data-mpi={index}>
                      <span className="ava-pillar-icon mx-auto mb-2 flex h-[46px] w-[46px] items-center justify-center rounded-full bg-primary/10 sm:mb-5 sm:h-14 sm:w-14" data-id="element-660" data-mp="24iqumhdep7r9">
                        <Icon className="h-[24px] w-[24px] text-primary sm:h-7 sm:w-7" aria-hidden="true" data-id="element-661" data-mp="2echdn6qrkpzk" />
                      </span>
                      <h3 className="ava-pillar-title mb-1 break-words text-[12px] font-bold leading-[1.15] text-navy sm:mb-3 sm:text-lg sm:leading-tight" data-id="element-662" data-mp="1lulwpqjfkh0d">
                        {item.title}
                      </h3>
                      {showMobileText && <p className="ava-pillar-text mx-auto max-w-xs break-words text-[10px] leading-[1.3] text-muted-foreground sm:hidden" data-id="element-663" data-mp="19d1bjh12jxqg">
                          {short}
                        </p>}
                      <p className="ava-pillar-full mx-auto hidden max-w-xs leading-relaxed text-muted-foreground sm:block sm:text-sm" data-id="element-664" data-mp="zcse5zvcc69h">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'confianza':
        {
          // Franja de confianza: cuatro puntos breves, en fila en escritorio y
          // en 2 × 2 en móvil. Todo el contenido es editable y ocultable.
          const items = cfg<{
            icon: string;
            text: string;
          }[]>(block, 'items', []);
          if (items.length === 0) return null;
          return <section key={block.id} className="px-4 pb-10 pt-2 sm:px-8 sm:pb-14" data-id="element-665" data-mp="wdjyp4tcd15s">
            <ul className="mx-auto grid max-w-5xl grid-cols-2 gap-4 rounded-2xl border border-border/70 bg-background px-5 py-6 shadow-soft sm:grid-cols-4 sm:gap-6 sm:px-8" data-id="element-666" data-mp="11cdgvajkpgca">
              {items.map((item, index) => {
                const Icon = ICONS[item.icon] ?? ShieldCheckIcon;
                return <li key={`${item.text}-${index}`} className="flex items-center justify-center gap-2.5 text-center" data-id="element-667" data-mp="rk7f593irkjw" data-mpi={index}>
                    <Icon className="h-4 w-4 flex-none text-primary" aria-hidden="true" data-id="element-668" data-mp="ilm21tgtgxqi" />
                    <span className="text-[12px] font-medium leading-snug text-muted-foreground sm:text-[13px]" data-id="element-669" data-mp="4m48l0skswat">
                      {item.text}
                    </span>
                  </li>;
              })}
            </ul>
          </section>;
        }
      case 'frase':
        return <section key={block.id} className="px-5 py-12 sm:px-8 sm:py-16" data-id="element-670" data-mp="1yyatirb9urgb">
            <div className="mx-auto flex max-w-4xl items-center gap-5 sm:gap-8" data-id="element-671" data-mp="2317723lyfxde">
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" data-id="element-672" data-mp="3nkkposiu8y6m" />
              <p className="flex-1 whitespace-pre-line text-center text-[11px] font-bold uppercase leading-relaxed tracking-[0.16em] text-navy sm:flex-none sm:text-sm sm:tracking-[0.18em]" data-id="element-673" data-mp="3jj7jghbu2sz6">
                {cfg(block, 'text', '')}
              </p>
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" data-id="element-674" data-mp="3sh1dtmq1fcjp" />
            </div>
          </section>;
      case 'estadisticas':
        {
          const items = cfg<{
            value: string;
            label: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-675" data-mp="k0yf46gwky7i">
            <div className="mx-auto grid max-w-4xl gap-10 text-center sm:grid-cols-3" data-id="element-676" data-mp="f4hgujfzp3gp">
              {items.map(item => <div key={item.label} data-id="element-677" data-mp="2lunygypf6ybv">
                  <p className="text-4xl font-semibold tracking-tight text-primary sm:text-5xl" data-id="element-678" data-mp="2gvkttfdcqu4f">
                    {item.value}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground" data-id="element-679" data-mp="ktr9o9iwc3q9">{item.label}</p>
                </div>)}
            </div>
          </section>;
        }
      case 'testimonios':
        {
          const items = cfg<{
            name: string;
            role: string;
            company: string;
            text: string;
            avatar?: string;
            result?: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-680" data-mp="13p7sas0ahr7f">
            <div className="mx-auto max-w-6xl" data-id="element-681" data-mp="1dgvf2flwik2k">
              <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-682" data-mp="1di6k0ir31xv9">
                {cfg(block, 'title', 'Testimonios')}
              </h2>
              <div className="grid gap-6 md:grid-cols-3" data-id="element-683" data-mp="1mibghxuqnbun">
                {items.map(item => <figure key={item.name} className={`flex flex-col rounded-2xl border border-border bg-background p-8 ${cardShadow}`} data-id="element-684" data-mp="1rgd9bahyupjb">
                    {item.result && <span className="mb-5 inline-flex w-fit items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-685" data-mp="3c08lupe1hsha">
                        {item.result}
                      </span>}
                    <blockquote className="flex-1 text-[15px] leading-relaxed text-foreground" data-id="element-686" data-mp="3303f8hgo6y46">
                      “{item.text}”
                    </blockquote>
                    <figcaption className="mt-7 flex items-center gap-3 border-t border-border pt-6" data-id="element-687" data-mp="3uo3wi0ecuq66">
                      {item.avatar ? <img src={item.avatar} alt="" loading="lazy" decoding="async" className="h-11 w-11 rounded-full object-cover" data-id="element-688" data-mp="3qo3de2gb9qss" /> : <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface text-sm font-semibold text-muted-foreground" data-id="element-689" data-mp="3luqt24io065u">
                          {item.name.charAt(0)}
                        </span>}
                      <span className="min-w-0 flex-1 text-sm" data-id="element-690" data-mp="dhbgko0zrh8h">
                        <span className="block truncate font-semibold text-foreground" data-id="element-691" data-mp="deyovj5flsi8">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground" data-id="element-692" data-mp="3mqmnh6lhl9wd">
                          {item.role}
                        </span>
                      </span>
                      <span className="hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block" data-id="element-693" data-mp="3cz8i4i6utfvb">
                        {item.company}
                      </span>
                    </figcaption>
                  </figure>)}
              </div>
            </div>
          </section>;
        }
      case 'logos_clientes':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className="px-5 py-14" data-id="element-694" data-mp="3voy8mt3l3fhf">
            <p className="mb-8 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground" data-id="element-695" data-mp="2g9r1j86c29eu">
              {cfg(block, 'title', '')}
            </p>
            <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-12 gap-y-6" data-id="element-696" data-mp="2k8rjypkwfa1b">
              {items.map(name => <span key={name} className="text-lg font-semibold tracking-tight text-muted-foreground/70" data-id="element-697" data-mp="27t08k48g5r3m">
                  {name}
                </span>)}
            </div>
          </section>;
        }
      case 'formulario':
        return <section key={block.id} id="formulario" className={`ava-reveal ava-reveal-3 scroll-mt-24 px-3 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-698" data-mp="2hrzf9h9njcnz">
            {/* Sin título ni subtítulo la tarjeta del formulario arranca sola,
                como en la plantilla oficial. En móvil el encabezado es más
                compacto (28 px de título en dos líneas como máximo y 15 px de
                subtítulo), con ancho amplio para evitar saltos raros. Ambos
                textos y sus tamaños móviles son editables desde Estilo → Móvil. */}
            {(cfg(block, 'title', '') || cfg(block, 'subtitle', '')) && <div className="mx-auto mb-6 w-[94%] max-w-2xl text-center sm:mb-12 sm:w-full" data-id="element-699" data-mp="16apwyy29eqo9">
                {cfg(block, 'title', '') && <h2 className="text-[22px] font-semibold leading-[1.2] tracking-tight text-navy sm:text-4xl sm:leading-tight" data-id="element-700" data-mp="6p43ncdp94ql">
                    {cfg(block, 'title', '')}
                  </h2>}
                {cfg(block, 'subtitle', '') && <p className="mt-2 w-[94%] mx-auto text-[14px] leading-[1.45] text-muted-foreground sm:mt-4 sm:w-full sm:text-lg sm:leading-relaxed" data-id="element-701" data-mp="bmdo0dm8eq7k">
                    {cfg(block, 'subtitle', '')}
                  </p>}
              </div>}
            {form ? <React.Suspense fallback={<div className="mx-auto h-[420px] max-w-2xl rounded-2xl border border-border bg-surface" aria-hidden="true" data-id="element-703" data-mp="3f0rlt71uy79e" />} data-id="element-702" data-mp="3f4fek3zhapn2">
                <DynamicForm form={form} onSubmit={onSubmit} preview={preview} showProgress={cfg(block, 'showProgress', 'si') !== 'no'} hidePrivacyNotice={cfg(block, 'showPrivacy', 'si') === 'no'} data-id="element-704" data-mp="3p1a9ye0gg9x4" />
              </React.Suspense> : <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-surface p-12 text-center text-sm text-muted-foreground" data-id="element-705" data-mp="2wd4qrvp1usze">
                Asocia un formulario a esta landing desde el editor.
              </div>}
          </section>;
      case 'garantias':
        {
          /* Acordeones de garantías: fondo blanco, esquinas suaves, sombra
             ligera, azul AVA en los iconos y título en azul oscuro. Todos los
             textos, iconos y la nota inferior se editan desde el constructor. */
          const items = cfg<GuaranteeItem[]>(block, 'items', []).filter(item => String(item.visible ?? 'si').toLowerCase() !== 'no' && String(item.title ?? '').trim() !== '');
          const note = flag(block, 'showNote', true) ? cfg(block, 'note', '') : '';
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-706" data-mp="1qif8rrgvcs2z">
            <div className={`mx-auto w-full max-w-3xl rounded-3xl border border-border/70 bg-background px-4 py-8 sm:px-10 sm:py-12 ${cardShadow}`} data-id="element-707" data-mp="a4fbko9ergjf">
              <h2 className="mb-6 text-center text-[30px] font-semibold leading-[1.2] tracking-tight text-navy sm:mb-10 sm:text-[32px] sm:leading-tight" data-id="element-708" data-mp="f5cu51ybyclg">
                {cfg(block, 'title', '¿Por qué implementar Sistema AVA?')}
              </h2>
              <React.Suspense fallback={<div className="h-[220px] rounded-2xl bg-surface" aria-hidden="true" data-id="element-710" data-mp="27237cv5gmq0t" />} data-id="element-709" data-mp="272w3712vrv46">
                <GuaranteeAccordion items={items} mode={cfg(block, 'accordionMode', 'uno') === 'varios' ? 'varios' : 'uno'} firstOpen={flag(block, 'firstOpen', false)} renderIcon={(name, className) => {
                  const Icon = ICONS[String(name ?? 'ShieldCheck')] ?? ShieldCheckIcon;
                  return <Icon className={className} aria-hidden="true" data-id="element-712" data-mp="1o5l69n4q1drw" />;
                }} data-id="element-711" data-mp="230re8fndnmnv" />
              </React.Suspense>
              {note && <p className="mt-5 text-center text-[11px] leading-[1.5] text-muted-foreground sm:mt-7 sm:text-xs" data-id="element-713" data-mp="1k4j8laqgd9s1">
                  {note}
                </p>}
            </div>
          </section>;
        }
      case 'faq':
        {
          /* Compatibilidad: las preguntas se guardan como `q`/`a`, pero algunas
             landings las tenían como `question`/`answer`, lo que hacía que el
             bloque se mostrara con las filas en blanco. Si no hay ninguna
             pregunta con texto, el bloque no se renderiza. */
          const items = cfg<{
            q?: string;
            a?: string;
            question?: string;
            answer?: string;
          }[]>(block, 'items', []).map(item => ({
            q: String(item.q ?? item.question ?? '').trim(),
            a: String(item.a ?? item.answer ?? '').trim()
          })).filter(item => item.q !== '');
          if (items.length === 0) return null;
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-714" data-mp="va3ru6u1a352">
            <div className="mx-auto max-w-3xl" data-id="element-715" data-mp="v7r75lww5eob">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-716" data-mp="21p5yk1kidb2z">
                {cfg(block, 'title', 'Preguntas frecuentes')}
              </h2>
              <div className="space-y-3" data-id="element-717" data-mp="2596bujai4ztb">
                {items.map(item => <details key={item.q} className="group rounded-xl border border-border bg-background px-5 py-4 transition-colors hover:border-primary/30" data-id="element-718" data-mp="2090got50wv6h">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium text-foreground marker:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-id="element-719" data-mp="2eacwn4k6owsd">
                      {item.q}
                      <ChevronDownIcon className="h-4 w-4 flex-none text-muted-foreground transition-transform duration-200 group-open:rotate-180" aria-hidden="true" data-id="element-720" data-mp="2j8eexn9scs2s" />
                    </summary>
                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-id="element-721" data-mp="2sx62lj7tcq7w">
                      {item.a}
                    </p>
                  </details>)}
              </div>
            </div>
          </section>;
        }
      case 'calendario':
        {
          // La reserva usa el calendario global de la empresa salvo que la
          // landing tenga uno propio. La lógica de reserva no cambia.
          const custom = cfg(block, 'calendarSource', 'global') === 'custom';
          const bookingUrl = String((custom ? cfg(block, 'url', '') : cfg(block, 'globalUrl', '') || cfg(block, 'url', '')) as string).trim();
          // Sin enlace válido el bloque no se muestra en la landing pública:
          // así nunca queda un hueco ni se carga ningún script de calendario.
          if (!bookingUrl && !preview) return null;
          const embedded = cfg(block, 'calendarMode', 'boton') === 'incrustado' && Boolean(bookingUrl);
          const embedHeight = Number(cfg(block, 'embedHeight', 700)) || 700;
          if (embedded) {
            return <section key={block.id} id="calendario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-722" data-mp="2xsifp8z1d5fn">
              <div className="mx-auto w-full max-w-3xl text-center" data-id="element-723" data-mp="2otx0nnsrw8jm">
                <h2 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-id="element-724" data-mp="2ovzrub5nw7px">
                  {cfg(block, 'title', 'Agenda una llamada')}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-725" data-mp="pm6l9zoz9kuu">
                  {cfg(block, 'text', '')}
                </p>
                <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-background shadow-soft" data-id="element-726" data-mp="gn310se2qzer">
                  <iframe src={bookingUrl} title={cfg(block, 'title', 'Calendario de reservas')} className="w-full" style={{
                    height: `${embedHeight}px`
                  }} loading="lazy" data-id="element-727" data-mp="1dbhqpuh9w3fb" />
                </div>
              </div>
            </section>;
          }
          return <section key={block.id} id="calendario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-728" data-mp="zdkq8mzp7cvd">
            <div className="mx-auto w-full max-w-3xl rounded-3xl border border-border bg-background p-7 text-center shadow-soft sm:p-12" data-id="element-729" data-mp="3p2v2v73u50z7">
              <CalendarDaysIcon className="mx-auto mb-5 h-8 w-8 text-primary" aria-hidden="true" data-id="element-730" data-mp="3k5d2w67spw3c" />
              <h2 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-id="element-731" data-mp="1zj50zt3mxgia">
                {cfg(block, 'title', 'Agenda una llamada')}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-732" data-mp="28k36ygxmvy3i">
                {cfg(block, 'text', '')}
              </p>
              <div className="mt-9 flex justify-center" data-id="element-733" data-mp="2ic8wzl4bs5g2">
                <Button size="lg" className="h-14 w-full rounded-xl px-8 sm:w-auto" onClick={() => {
                  if (bookingUrl) window.open(bookingUrl, '_blank', 'noopener');
                }} data-id="element-734" data-mp="2idtqr8itfkcv">
                  {cfg(block, 'ctaLabel', 'Reservar hora')}
                </Button>
              </div>
              {!bookingUrl && <p className="mt-5 text-xs text-muted-foreground" data-id="element-735" data-mp="xvvvlxxrn41u">
                  Conecta Google Calendar en Integraciones o añade una URL de
                  reserva a este bloque.
                </p>}
            </div>
          </section>;
        }
      case 'whatsapp':
        {
          /*
            Bloque histórico de WhatsApp flotante. Convive con el elemento
            flotante configurable (`pageConfig.floating.whatsapp`), que es el
            que se activa y desactiva desde «Elementos flotantes». Si ese está
            activado, este bloque no se renderiza: de lo contrario aparecían
            dos botones y apagar el interruptor no quitaba el del bloque, que
            es exactamente el síntoma de «OFF y sigue apareciendo». Sin número
            tampoco se renderiza: el enlace no llevaría a ningún chat.
          */
          if (config.floating?.whatsapp?.enabled) return null;
          const blockNumber = String(cfg(block, 'number', '')).replace(/\D/g, '');
          if (!blockNumber) return null;
          return <a key={block.id} href={`https://wa.me/${blockNumber}?text=${encodeURIComponent(cfg(block, 'message', ''))}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105" aria-label="Contactar por WhatsApp" data-id="element-736" data-mp="1kgn5ypvuok8a">
              <MessageCircleIcon className="h-6 w-6" data-id="element-737" data-mp="1e396pj31k4bl" />
            </a>;
        }
      case 'cta_final':
        {
          // Con número de WhatsApp el botón abre el chat; si no, baja al formulario.
          const waNumber = String(cfg(block, 'whatsappNumber', '')).replace(/\D/g, '');
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-738" data-mp="2box1y0nrgglj">
            <div className="mx-auto max-w-3xl text-center" data-id="element-739" data-mp="1x00ua4pm95lg">
              <h2 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-740" data-mp="21z3nmrw8hndl">
                {cfg(block, 'title', '')}
              </h2>
              <p className="mt-4 text-lg text-muted-foreground" data-id="element-741" data-mp="2pnwpf4zjepg4">
                {cfg(block, 'text', '')}
              </p>
              <div className="mx-auto mt-10 flex max-w-[380px] justify-center" data-id="element-742" data-mp="akkvkk4zrde">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', 'Quiero empezar')} onClick={() => {
                  if (waNumber) {
                    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(cfg(block, 'whatsappMessage', ''))}`, '_blank', 'noopener');
                    return;
                  }
                  scrollToForm();
                }} data-id="element-743" data-mp="8ggz2m8msd4" />
              </div>
            </div>
          </section>;
        }
      case 'footer':
        {
          // Solo se muestran las redes con URL configurada.
          const socials = cfg<{
            network: string;
            url: string;
          }[]>(block, 'socials', []).filter(s => s.url?.trim());
          return <footer key={block.id} className="border-t border-border px-5 py-12 sm:px-8" data-id="element-744" data-mp="3reizaq8g1tlc">
            <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-745" data-mp="12r49p6pqqz2k">
              <div className="flex flex-col items-center gap-4 sm:items-start" data-id="element-746" data-mp="2r8fjv53vay2c">
                {brandMark}
              </div>
              <div className="flex flex-col items-center gap-2 text-center text-xs text-muted-foreground" data-id="element-747" data-mp="2n6u1x5ch7tla">
                <p data-id="element-748" data-mp="2ifc0yhagdt0b">{cfg(block, 'text', '')}</p>
                <div className="flex flex-wrap justify-center gap-x-5 gap-y-1" data-id="element-749" data-mp="2ie0v8bxg6b6k">
                  {cfg<string[]>(block, 'links', []).map(link => <span key={link} className="cursor-pointer hover:text-foreground" data-id="element-750" data-mp="28ipfbqaayxx8">
                      {link}
                    </span>)}
                </div>
              </div>
              {socials.length > 0 && <div className="flex gap-3 text-muted-foreground" data-id="element-751" data-mp="1zgpwqtjn3ls6">
                  {socials.map((social, index) => {
                  const network = SOCIAL_NETWORKS[social.network];
                  const Icon = network?.icon ?? LinkIcon;
                  return <a key={`${social.network}-${index}`} href={social.url} target="_blank" rel="noopener noreferrer" aria-label={network?.label ?? social.network} className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-primary/40 hover:text-primary" data-id="element-752" data-mp="3ttg0s43q2bda" data-mpi={index}>
                        <Icon className="h-4 w-4" aria-hidden="true" data-id="element-753" data-mp="2uyrm3okjkji" />
                      </a>;
                })}
                </div>}
            </div>
          </footer>;
        }
      case 'servicios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-754" data-mp="zkpno7cidfrf">
            <div className="mx-auto max-w-6xl" data-id="element-755" data-mp="lkon6t05oach">
              {cfg(block, 'title', '') && <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-756" data-mp="qjrpw6sny4ha">
                  {cfg(block, 'title', '')}
                </h2>}
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-757" data-mp="3bb1xc3w4ez57">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className={`grid gap-6 md:grid-cols-3 ${cfg(block, 'title', '') || cfg(block, 'subtitle', '') ? 'mt-14' : ''}`} data-id="element-758" data-mp="2ys6dg8z67yb1">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? BriefcaseIcon;
                  return <article key={`${item.title}-${index}`} className={`rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-759" data-mp="37u5rcsew2m9r" data-mpi={index}>
                      <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10" data-id="element-760" data-mp="2g3j95sotjcyi">
                        <Icon className="h-5 w-5 text-primary" data-id="element-761" data-mp="2tv62bjngs5d2" />
                      </span>
                      <h3 className="mb-2 text-base font-semibold text-navy" data-id="element-762" data-mp="2p0a0jrsfe3mr">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-763" data-mp="1we8u8gwwcw1g">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'caracteristicas':
        {
          const items = cfg<{
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-764" data-mp="2oq40rjn8ofw7">
            <div className="mx-auto max-w-4xl" data-id="element-765" data-mp="2sq4e10ylql1o">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-766" data-mp="2xq0h8xzh68g6">
                {cfg(block, 'title', 'Características')}
              </h2>
              <ul className="grid gap-x-10 gap-y-6 sm:grid-cols-2" data-id="element-767" data-mp="32n92x8ju0um6">
                {items.map((item, index) => <li key={`${item.title}-${index}`} className="flex gap-3" data-id="element-768" data-mp="37lu4bbatlkc7" data-mpi={index}>
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10" data-id="element-769" data-mp="18cx1crwrejin">
                      <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-770" data-mp="1dc05ma4x0lpk" />
                    </span>
                    <span data-id="element-771" data-mp="3qm9sfsfhxmuv">
                      <span className="block text-[15px] font-medium text-foreground" data-id="element-772" data-mp="3lt6sn2k4lh68">
                        {item.title}
                      </span>
                      <span className="block text-sm leading-relaxed text-muted-foreground" data-id="element-773" data-mp="3hsd9emhmoqjo">
                        {item.text}
                      </span>
                    </span>
                  </li>)}
              </ul>
            </div>
          </section>;
        }
      case 'galeria':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-774" data-mp="4c5or0otgp9v">
            <div className="mx-auto max-w-6xl" data-id="element-775" data-mp="3vkbbwegu3o6v">
              {cfg(block, 'title', '') && <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-776" data-mp="2p36b9om362yy">
                  {cfg(block, 'title', '')}
                </h2>}
              {items.length === 0 ? <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-surface py-16 text-sm text-muted-foreground" data-id="element-777" data-mp="2lls0gs44m3jz">
                  <ImageIcon className="h-6 w-6" aria-hidden="true" data-id="element-778" data-mp="2qjtrq0bph96j" />
                  Añade imágenes desde el editor
                </div> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-779" data-mp="2hjwxlj6jri43">
                  {items.map((url, index) => <img key={`${url}-${index}`} src={url} alt="" loading="lazy" decoding="async" className={`aspect-[4/3] w-full rounded-2xl object-cover ${cardShadow}`} data-id="element-780" data-mp="22v0qppg88b52" data-mpi={index} />)}
                </div>}
            </div>
          </section>;
        }
      case 'precios':
        {
          const items = cfg<{
            name: string;
            price: string;
            period: string;
            features: string;
            featured: string;
            ctaLabel: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-781" data-mp="1ovspkr0i6sy8">
            <div className="mx-auto max-w-5xl" data-id="element-782" data-mp="158o6hfvdmc1f">
              <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-783" data-mp="15bk4133baypc">
                {cfg(block, 'title', 'Planes')}
              </h2>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-784" data-mp="1f5kg5ox0h47k">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className="mt-14 grid gap-6 md:grid-cols-2" data-id="element-785" data-mp="1o7aap3m6wj9k">
                {items.map((plan, index) => {
                  const featured = Boolean(plan.featured);
                  return <article key={`${plan.name}-${index}`} className={`flex flex-col rounded-2xl border bg-background p-8 ${featured ? 'border-primary shadow-soft' : 'border-border'}`} data-id="element-786" data-mp="1xyogtu8nchbo" data-mpi={index}>
                      {featured && <span className="mb-5 inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-787" data-mp="26y1pikoy51vc">
                          Recomendado
                        </span>}
                      <h3 className="text-base font-semibold text-navy" data-id="element-788" data-mp="39cz0cuu4ee4m">{plan.name}</h3>
                      <p className="mt-4 flex items-baseline gap-2" data-id="element-789" data-mp="3ear2ehohbgmj">
                        <span className="text-4xl font-semibold tracking-tight text-navy" data-id="element-790" data-mp="3ed3l4x229v0n">
                          {plan.price}
                        </span>
                        <span className="text-sm text-muted-foreground" data-id="element-791" data-mp="3nank1zcgbf83">
                          {plan.period}
                        </span>
                      </p>
                      <ul className="mt-7 flex-1 space-y-3" data-id="element-792" data-mp="2vk9b8hjaot3a">
                        {String(plan.features ?? '').split('\n').filter(Boolean).map(feature => <li key={feature} className="flex gap-2.5 text-sm text-muted-foreground" data-id="element-793" data-mp="2zktcnxawiiv8">
                              <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-primary" data-id="element-794" data-mp="34enuswauyps7" />
                              {feature}
                            </li>)}
                      </ul>
                      <Button className="mt-8 w-full" variant={featured ? 'primary' : 'secondary'} onClick={scrollToForm} data-id="element-795" data-mp="2bvh1irw0k0qa">
                        {plan.ctaLabel || 'Empezar'}
                      </Button>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'contacto':
        return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-796" data-mp="1qdhwzok90cfi">
            <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background p-10 text-center shadow-soft" data-id="element-797" data-mp="1qfuanpiz40mr">
              <h2 className="text-2xl font-semibold tracking-tight text-navy sm:text-3xl" data-id="element-798" data-mp="1gn6i56z10itu">
                {cfg(block, 'title', 'Contacto')}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground" data-id="element-799" data-mp="1bmisopqg4a1q">
                {cfg(block, 'text', '')}
              </p>
              <dl className="mt-8 grid gap-4 sm:grid-cols-3" data-id="element-800" data-mp="12nxaivovyx1h">
                {[{
                icon: AtSignIcon,
                value: cfg(block, 'email', '')
              }, {
                icon: PhoneIcon,
                value: cfg(block, 'phone', '')
              }, {
                icon: MapPinIcon,
                value: cfg(block, 'address', '')
              }].filter(entry => entry.value).map(entry => <div key={entry.value} className="flex flex-col items-center gap-2 rounded-xl bg-surface px-4 py-5" data-id="element-801" data-mp="mi1k69srw8a7">
                      <entry.icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-802" data-mp="hi6s42x9hcnk" />
                      <span className="break-words text-sm text-foreground" data-id="element-803" data-mp="di4xgdpy1jhs">
                        {entry.value}
                      </span>
                    </div>)}
              </dl>
            </div>
          </section>;
      case 'filtro':
        {
          /* Comparativa de cualificación. Las tarjetas son semitransparentes,
             así que el fondo global (chispas, red, aura…) se sigue viendo a
             través en lugar de quedar tapado por una caja blanca. */
          const items = cfg<{
            lado?: string;
            text?: string;
          }[]>(block, 'items', []).filter(item => String(item.text ?? '').trim() !== '');
          const yes = items.filter(item => String(item.lado ?? 'si').toLowerCase() !== 'no');
          const no = items.filter(item => String(item.lado ?? 'si').toLowerCase() === 'no');
          const columns: {
            key: string;
            title: string;
            rows: typeof items;
            positive: boolean;
          }[] = [{
            key: 'si',
            title: cfg(block, 'yesTitle', 'ES PARA TI SI...'),
            rows: yes,
            positive: true
          }, {
            key: 'no',
            title: cfg(block, 'noTitle', 'NO ES PARA TI SI...'),
            rows: no,
            positive: false
          }];
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-804" data-mp="1whcp91fipgkq">
            <div className="mx-auto w-full max-w-5xl" data-id="element-805" data-mp="z1p2dnapfkyc">
              {cfg(block, 'eyebrow', '') && <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-primary" data-id="element-806" data-mp="13zqhxy8x9252">
                  {cfg(block, 'eyebrow', '')}
                </p>}
              <h2 className="text-center text-[26px] font-semibold leading-[1.2] tracking-tight text-navy sm:text-4xl" data-id="element-807" data-mp="rxp9bmhgtv2k">
                {cfg(block, 'title', '')}
              </h2>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-3 max-w-2xl text-center text-[15px] leading-relaxed text-muted-foreground sm:mt-4 sm:text-lg" data-id="element-808" data-mp="10qaqiy5g7tpa">
                  {cfg(block, 'subtitle', '')}
                </p>}
              {/* Escritorio: dos columnas. Móvil: «Es para ti» arriba. */}
              <div className="mt-9 grid gap-5 sm:mt-12 md:grid-cols-2 md:gap-6" data-id="element-809" data-mp="917kpdxwybaj">
                {columns.filter(column => column.rows.length > 0).map(column => <div key={column.key} className={`rounded-3xl border p-6 sm:p-8 ${cardShadow} ${column.positive ? 'border-primary/25 bg-background/80' : 'border-border/70 bg-surface/70'}`} data-id="element-810" data-mp="hz9q05oojq3m">
                    <p className={`mb-5 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em] sm:text-[13px] ${column.positive ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-811" data-mp="1tfhnvtt62d8y">
                      <span className={`flex h-6 w-6 flex-none items-center justify-center rounded-full ${column.positive ? 'bg-primary/10 text-primary' : 'bg-foreground/5 text-muted-foreground'}`} aria-hidden="true" data-id="element-812" data-mp="3gbxpdck4e214">
                        {column.positive ? <CheckIcon className="h-3.5 w-3.5" data-id="element-813" data-mp="37b9h6yheqvw5" /> : <XIcon className="h-3.5 w-3.5" data-id="element-814" data-mp="3v0aogiei80zu" />}
                      </span>
                      <span className="min-w-0 break-words" data-id="element-815" data-mp="3v2ofmg62ct24">{column.title}</span>
                    </p>
                    <ul className="space-y-4" data-id="element-816" data-mp="3laio17jq4dng">
                      {column.rows.map((item, index) => <li key={`${column.key}-${index}`} className="flex items-start gap-3" data-id="element-817" data-mp="3l8enmzl19wrx" data-mpi={index}>
                          <span className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full ${column.positive ? 'bg-primary/10 text-primary' : 'bg-foreground/5 text-muted-foreground'}`} aria-hidden="true" data-id="element-818" data-mp="3q7hgllnqocjj">
                            {column.positive ? <CheckIcon className="h-3 w-3" data-id="element-819" data-mp="2u4c0b4tjsda2" /> : <XIcon className="h-3 w-3" data-id="element-820" data-mp="2u5n2x1b9bezl" />}
                          </span>
                          <span className="min-w-0 break-words text-[15px] leading-relaxed text-foreground" data-id="element-821" data-mp="2p47jtesn3iju">
                            {item.text}
                          </span>
                        </li>)}
                    </ul>
                  </div>)}
              </div>
            </div>
          </section>;
        }
      case 'historia':
        {
          /* Contenido estático: sin librerías, sin consultas y con la foto en
             carga diferida porque siempre queda por debajo del pliegue. */
          const variant = cfg(block, 'variant', 'editorial');
          const photo = cfg(block, 'photo', '');
          const photoSize = Number(cfg(block, 'photoSize', 320)) || 320;
          const shape = cfg(block, 'photoShape', 'redondeada');
          const radius = shape === 'circulo' ? '9999px' : shape === 'cuadrada' ? '0px' : '24px';
          const paragraphs = String(cfg(block, 'text', '')).split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
          const textWidth = Number(cfg(block, 'textWidth', 680)) || 680;
          const ctaLabel = cfg(block, 'ctaLabel', '');
          const ctaUrl = cfg(block, 'ctaUrl', '');
          const picture = photo ? <img src={photo} alt={cfg(block, 'name', '') || cfg(block, 'title', '')} loading="lazy" decoding="async" width={photoSize} height={photoSize} className="w-full max-w-full object-cover" style={{
            maxWidth: `${photoSize}px`,
            aspectRatio: '1 / 1',
            borderRadius: radius
          }} data-id="element-822" data-mp="gxvmnxr75jvc" /> : null;
          const identity = (cfg(block, 'name', '') || cfg(block, 'role', '')) && <div className="mt-4" data-id="element-823" data-mp="10lmk5flq7jp1">
              {cfg(block, 'name', '') && <p className="text-base font-semibold text-navy" data-id="element-824" data-mp="10n62rcuuu6tz">
                  {cfg(block, 'name', '')}
                </p>}
              {cfg(block, 'role', '') && <p className="text-sm text-muted-foreground" data-id="element-825" data-mp="19mt45ynl3cn1">
                  {cfg(block, 'role', '')}
                </p>}
            </div>;
          const story = <div className="space-y-4" data-id="element-826" data-mp="31nuf2b963y4x">
              {paragraphs.map((paragraph, index) => <p key={`p-${index}`} className="text-[15px] leading-relaxed text-foreground sm:text-[17px] sm:leading-[1.75]" data-id="element-827" data-mp="31lrewyeawomh" data-mpi={index}>
                  {paragraph}
                </p>)}
              {ctaLabel && ctaUrl && <a href={ctaUrl} className="mt-2 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-primary px-7 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5" data-id="element-828" data-mp="3amfuhxjcex1h">
                  {ctaLabel}
                </a>}
            </div>;
          const header = <>
              {cfg(block, 'eyebrow', '') && <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary" data-id="element-829" data-mp="3tg1w3csl5cju">
                  {cfg(block, 'eyebrow', '')}
                </p>}
              {cfg(block, 'title', '') && <h2 className="text-[26px] font-semibold leading-[1.2] tracking-tight text-navy sm:text-4xl" data-id="element-830" data-mp="1z20xmt0y6rka">
                  {cfg(block, 'title', '')}
                </h2>}
            </>;
          if (variant === 'lado') {
            return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-831" data-mp="jjvxxtbkjhpl">
              <div className="mx-auto grid w-full max-w-5xl items-center gap-8 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-12" data-id="element-832" data-mp="5q4qqrlb89x1">
                {picture && <div className="flex justify-center md:justify-start" data-id="element-833" data-mp="3rx1sm9icp5sc">{picture}</div>}
                <div className="min-w-0" data-id="element-834" data-mp="r0ic367mg7r">
                  {header}
                  <div className="mt-5" data-id="element-835" data-mp="p78kf0avfvj">{story}</div>
                  {identity}
                </div>
              </div>
            </section>;
          }
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-836" data-mp="3mzjn6t3jl8p3">
            <div className="mx-auto w-full max-w-3xl text-center" data-id="element-837" data-mp="1h5a20jzibgs9">
              {header}
              {picture && <div className="mt-8 flex justify-center" data-id="element-838" data-mp="1l63s9k0f97ob">{picture}</div>}
              {identity}
              <div className="mx-auto mt-7 text-left" style={{
                maxWidth: `${textWidth}px`
              }} data-id="element-839" data-mp="8wqik0d8j94p">
                {story}
              </div>
            </div>
          </section>;
        }
      default:
        return null;
    }
  }

  /** En el constructor cada bloque es seleccionable haciendo clic sobre él. */
  function renderEditable(block: LandingBlock) {
    const content = renderBlock(block);
    const mobile = mobileStyle(block);
    const style = {
      ...wrapperStyle(block),
      ...mobile.vars
    };
    const hasStyle = Object.values(style).some(v => v !== undefined);
    if (!onSelectBlock) {
      return hasStyle || mobile.className ? <div key={block.id} style={style} className={mobile.className} data-id="element-840" data-mp="37fnqyalky0hq">
          {content}
        </div> : content;
    }
    const selected = selectedBlockId === block.id;
    return <div key={block.id} role="button" tabIndex={0} onClick={() => onSelectBlock(block.id)} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectBlock(block.id);
      }
    }} data-block-id={block.id} style={style} className={`relative cursor-pointer outline-none transition-shadow ${mobile.className} ${selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:ring-2 hover:ring-primary/30'}`} data-id="element-841" data-mp="1akrkb8kiznch">
        {selected && <span className="pointer-events-none absolute left-3 top-3 z-40 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground" data-id="element-842" data-mp="1rykh4lhvlhot">
            {blockLabel(block.type)}
          </span>}
        {content}
      </div>;
  }
  // Efecto de fondo opcional. Con «Normal» (el valor por defecto) la clase es
  // vacía y no se monta ninguna capa: coste exactamente cero.
  const bgClass = solo ? '' : backgroundClass(config.background);
  return <div ref={rootRef} className={`relative w-full bg-background ${bgClass ? 'ava-bg-live' : ''} ${viewport === 'mobile' ? 'ava-viewport-mobile' : viewport === 'tablet' ? 'ava-viewport-tablet' : ''}`} style={{
    '--primary': config.primaryColor,
    fontFamily: `${config.fontFamily}, Inter, sans-serif`,
    // Fondo apenas perceptible hacia azul muy claro, sin perder legibilidad.
    ...(theme === 'blanco_premium' ? {
      background: 'linear-gradient(180deg,#FFFFFF 0%,#F8FAFD 45%,#F4F7FD 100%)'
    } : {})
  } as React.CSSProperties} data-id="element-843" data-mp="2fkzbrrd85h3f">
      {bgClass && <div className={bgClass} style={{
      '--bg-opacity': config.background?.opacity ?? 6
    } as React.CSSProperties} aria-hidden="true" data-id="element-844" data-mp="67ngfg7iknr2" />}
      <div className="ava-blocks relative z-[1]" data-id="element-845" data-mp="b7jgx5h05x1v">
        {blocks.map(renderEditable)}
        {blocks.length === 0 && <div className="flex min-h-[300px] items-center justify-center p-10 text-center text-sm text-muted-foreground" data-id="element-846" data-mp="3tdxxlc6nol3y">
            Esta landing no tiene bloques visibles. Activa alguno desde el editor.
          </div>}
      </div>
      {/*
        Elementos flotantes opcionales: no montan nada si están desactivados.
        En la vista previa se fuerza el dispositivo simulado; en la landing
        pública se deja sin definir para que se mida el ancho real del móvil.
       */}
      {!solo && (config.floating?.whatsapp?.enabled || config.floating?.activity?.enabled) && <React.Suspense fallback={null} data-id="element-847" data-mp="2fhdbkagajdhm">
          <FloatingElements config={config.floating} viewport={preview ? viewport : undefined} data-id="element-848" data-mp="2fig38klftjyl" />
        </React.Suspense>}
    </div>;
}